import {
  flexRender,
  Table as TableInstance,
  Header,
  Row,
  Cell,
  HeaderGroup,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@wellesley/ui'
import { ArrowDown, ArrowDownUp, ArrowUp, Loader2 } from 'lucide-react'
import { useMemo, useRef, useState, useEffect, memo } from 'react'
import CustomTooltip from '../../CustomTooltip'
import { useColumnResizing } from './useColumnResizing'

interface DataTableProps<TData> {
  table: TableInstance<TData>
  isLoading: boolean
  className?: string
}

interface TableHeaderProps<TData> {
  header: Header<TData, unknown>
}

interface TableHeaderGroupProps<TData> {
  headerGroup: HeaderGroup<TData>
}

interface TableCellProps<TData> {
  cell: Cell<TData, unknown>
  className?: string
}

interface TableRowProps<TData> {
  row: Row<TData>
}

interface TableStateProps {
  colSpan: number
}

type TableCellMeta = {
  cellClassName?: string
}

const getPinnedStyles = (isPinned: boolean | string, start: number) => {
  if (!isPinned) return {}

  return {
    ...(isPinned === 'left' ? { left: start } : {}),
    ...(isPinned === 'right' ? { right: start } : {}),
    zIndex: isPinned ? (isPinned === 'left' ? 20 : 10) : 0,
  }
}

const TableHeaderCell = <TData,>({ header }: TableHeaderProps<TData>) => {
  const [isHovered, setIsHovered] = useState(false)
  const pinnedStyles = getPinnedStyles(
    header.column.getIsPinned(),
    header.getStart(),
  )

  const {
    isResizing,
    handleResizeStart,
    handleHeaderClick,
    resetColumnSize,
    preventEvent,
  } = useColumnResizing(header)

  const showSortingIndicator =
    header.column.getCanSort() && (header.column.getIsSorted() || isHovered)

  return (
    <TableHead
      key={header.id}
      className={cn('relative select-none', {
        'sticky left-0 z-10  border-gray-200': header.column.getIsPinned(),
        'cursor-pointer hover:bg-gray-100':
          header.column.getCanSort() && !isResizing,
      })}
      style={{
        ...pinnedStyles,
        width: `${header.column.getSize()}px`,
      }}
      onClick={handleHeaderClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="gap-xxs flex items-center overflow-hidden text-ellipsis whitespace-nowrap">
        {flexRender(header.column.columnDef.header, header.getContext())}
        {showSortingIndicator && (
          <div className="p-xxxs rounded-xs hover:bg-gray-100">
            <SortingIndicator
              sortDirection={header.column.getIsSorted() as string}
            />
          </div>
        )}
      </div>

      {header.column.getCanResize() && (
        <div
          onMouseDown={handleResizeStart}
          onClick={preventEvent}
          onDoubleClick={resetColumnSize}
          className={cn(
            'absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent transition hover:bg-gray-100',
            isResizing && 'w-1 bg-gray-900',
          )}
        />
      )}
    </TableHead>
  )
}

const SortingIndicator = memo(function SortingIndicator({
  sortDirection,
}: {
  sortDirection: string
}) {
  if (sortDirection === 'asc') return <ArrowUp size={16} />
  if (sortDirection === 'desc') return <ArrowDown size={16} />
  return <ArrowDownUp size={16} />
})

const TableHeaderRow = <TData,>({
  headerGroup,
}: TableHeaderGroupProps<TData>) => {
  return (
    <TableRow key={headerGroup.id}>
      {headerGroup.headers.map((header) => (
        <TableHeaderCell key={header.id} header={header} />
      ))}
    </TableRow>
  )
}

const TableDataCell = <TData,>({ cell, className }: TableCellProps<TData>) => {
  const pinnedStyles = getPinnedStyles(
    cell.column.getIsPinned(),
    cell.column.getStart(),
  )

  const cellValue = cell.getValue() as string
  const isString = typeof cellValue === 'string'
  const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext())

  const cellRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    if (cellRef.current && isString) {
      const element = cellRef.current
      setIsOverflowing(element.scrollWidth > element.clientWidth)
    }
  }, [cellValue, isString])

  return (
    <TableCell
      key={cell.id}
      className={cn(
        'overflow-hidden',
        cell.column.getIsPinned() &&
          'sticky left-0 z-10 shadow-[inset_-2px_0_0_0_var(--alpha-white-80)]',
        className,
      )}
      style={{
        ...pinnedStyles,
        width: `${cell.column.getSize()}px`,
      }}
    >
      {isString ? (
        <div
          ref={cellRef}
          className="overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {isOverflowing ? (
            <CustomTooltip tooltipText={cellValue}>{cellContent}</CustomTooltip>
          ) : (
            cellContent
          )}
        </div>
      ) : (
        <div className="overflow-hidden">{cellContent}</div>
      )}
    </TableCell>
  )
}

const TableDataRow = <TData,>({ row }: TableRowProps<TData>) => {
  return (
    <TableRow
      key={row.id}
      className={cn(
        'group border-b border-gray-200',
        row.getIsSelected() && 'bg-gray-100',
      )}
    >
      {row.getVisibleCells().map((cell) => {
        const cellClassName = (cell.column.columnDef.meta as TableCellMeta)
          ?.cellClassName

        return (
          <TableDataCell key={cell.id} cell={cell} className={cellClassName} />
        )
      })}
    </TableRow>
  )
}

const TableLoadingOverlay = memo(function TableLoadingOverlay() {
  return (
    <div className="bg-alpha-white-80 pointer-events-auto absolute inset-0 z-50 flex items-center justify-center">
      <Loader2 className="size-m animate-spin text-gray-500" />
    </div>
  )
})

const TableEmptyState = memo(function TableEmptyState({
  colSpan,
}: TableStateProps) {
  return (
    <TableRow>
      <TableCell
        data-testid="empty-state"
        colSpan={colSpan}
        className="h-24 text-center"
        style={{ width: '100%' }}
      />
    </TableRow>
  )
})

export const DataTable = <TData,>({
  table,
  isLoading,
  className,
}: DataTableProps<TData>) => {
  const colSpan = useMemo(
    () => table.getHeaderGroups()[0]?.headers.length || 1,
    [table],
  )

  const selectedRows = useMemo(() => {
    return table.getSelectedRowModel().rows
  }, [table.getSelectedRowModel().rows])

  const rows = useMemo(() => {
    return table
      .getRowModel()
      .rows.map((row) => <TableDataRow key={row.id} row={row} />)
  }, [table.getRowModel().rows, selectedRows])

  const headerGroups = useMemo(() => {
    return table
      .getHeaderGroups()
      .map((headerGroup) => (
        <TableHeaderRow key={headerGroup.id} headerGroup={headerGroup} />
      ))
  }, [table.getHeaderGroups(), selectedRows])

  const isEmpty = table.getRowModel().rows?.length === 0
  return (
    <div
      className={cn(
        'relative w-full',
        {
          'pointer-events-none overflow-hidden': isLoading,
        },
        className,
      )}
    >
      <Table className="w-full table-fixed border-t border-gray-200">
        <TableHeader className="sticky top-0 z-30 border-b border-t border-gray-200">
          {headerGroups}
        </TableHeader>

        <TableBody
          className={cn({
            'border-b border-gray-200': !isEmpty,
          })}
        >
          {!isEmpty ? rows : <TableEmptyState colSpan={colSpan} />}
        </TableBody>
      </Table>

      {isLoading && <TableLoadingOverlay />}
    </div>
  )
}
